import { JxsComponent } from '@jxs/component';
import { JxsScrollTrigger, gsap } from '@jxs/gsap';
import { debounce } from '@jxs/utilities';

/**
 * @typedef {Object} JxsStVideoHeroRefs
 * @property {HTMLElement} top
 * @property {HTMLElement} viewport
 * @property {SVGElement} overlay
 * @property {SVGTextElement} overlayText
 */

/**
 * @typedef {Object} JxsStVideoHeroConfig
 * @property {number} maxWidth - 最大宽度限制（像素）
 * @property {number} minFontSize - 最小字体大小
 * @property {number} maxFontSize - 最大字体大小
 * @property {number} textWidthRatio - 文字占viewBox宽度的比例
 */

/** @extends {JxsComponent<JxsStVideoHeroRefs>} */
class JxsStVideoHero extends JxsComponent {
  /**
   * 配置参数
   * @type {JxsStVideoHeroConfig}
   */
  #CONFIG = {
    maxWidth: 1000,
    minFontSize: 4,
    maxFontSize: 20,
    textWidthRatio: 0.7
  };

  /** @type {HTMLCanvasElement|null} */
  #measureCanvas = null;

  /** @type {JxsScrollTrigger|null} */
  #scroller = null;

  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.init();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroy();
  }

  /**
   * 窗口大小变化处理
   */
  #onResize = debounce(() => {
    this.#adjustTextSize();
  }, 200);

  /**
   * 初始化组件
   */
  init() {
    this.destroy();

    if (!this.refs.overlayText) {
      console.warn('[JxsStVideoHero] 未找到overlayText元素');
      return;
    }

    // 创建测量用的canvas
    this.#measureCanvas = document.createElement('canvas');

    // 初始调整文字大小
    this.#adjustTextSize();

    // 初始化ScrollTrigger
    this.#initScrollTrigger();

    // 监听窗口resize
    window.addEventListener('resize', this.#onResize);
  }

  /**
   * 初始化ScrollTrigger
   */
  #initScrollTrigger() {
    if (!this.refs.viewport || !this.refs.overlay) return;

    this.#scroller = new JxsScrollTrigger(this.refs.viewport, {
      trigger: this.refs.viewport,
      endHold: 500,
      scrub: 0.5
    });

    const tl = this.#scroller.timeline;


    // 放大动画：从0秒开始，持续1.5秒
    tl.to(this.refs.overlay, {
      scale: 18,
      transformOrigin: 'center center',
      ease: 'none',
      duration: 1.5
    }, 0);

    // 淡出动画：从1.35秒开始（90%位置），持续0.5秒
    tl.to(this.refs.overlay, {
      opacity: 0,
      ease: 'none',
      duration: 0.5
    }, '<0.4');

    tl.to(this.refs.video, {
      scale: 1,
      transformOrigin: 'center center',
      duration: 0.5
    }, '>-0.5');

    if (this.refs.top.children.length > 0) {
      tl.to(this.refs.top, {
        y: 0,
        opacity: 1,
        duration: 0.5
      }, '<0.3');
    } else {
      this.refs.top.remove();
    }
  }

  /**
   * 动态调整文字大小
   */
  #adjustTextSize() {
    const textElement = this.refs.overlayText;
    const container = this.refs.viewport;

    if (!textElement || !container) return;

    const containerWidth = container.offsetWidth;

    // 如果容器宽度小于等于最大宽度，直接使用容器宽度
    // 如果容器宽度大于最大宽度，使用最大宽度作为基准
    const referenceWidth = Math.min(containerWidth, this.#CONFIG.maxWidth);

    // 获取文字内容
    const textContent = textElement.textContent.trim();

    // 使用canvas测量文字宽度
    if (!this.#measureCanvas) return;
    
    const context = this.#measureCanvas.getContext('2d');
    if (!context) return;
    
    context.font = '900 12px sans-serif'; // 使用相同的字体权重和基准字号

    // 测量在font-size=12时的文字宽度
    const textWidthAtBaseSize = context.measureText(textContent).width;

    // 计算合适的字体大小
    // SVG viewBox是100x100，我们希望文字占viewBox的合适比例
    const targetTextWidth = 100 * this.#CONFIG.textWidthRatio;

    // 根据容器实际宽度调整缩放比例
    const scale = referenceWidth / this.#CONFIG.maxWidth;
    const adjustedTargetWidth = targetTextWidth * Math.max(scale, 0.3); // 最小缩放到30%

    // 计算字体大小
    const fontSize = (adjustedTargetWidth / textWidthAtBaseSize) * 12;

    // 设置合理的字体大小范围
    const finalFontSize = Math.max(
      this.#CONFIG.minFontSize,
      Math.min(this.#CONFIG.maxFontSize, fontSize)
    );

    textElement.setAttribute('font-size', finalFontSize.toFixed(2));
  }

  /**
   * 销毁组件
   */
  destroy() {
    // 清理ScrollTrigger
    if (this.#scroller) {
      this.#scroller.destroy();
      this.#scroller = null;
    }

    // 清理canvas
    if (this.#measureCanvas) {
      this.#measureCanvas = null;
    }

    // 取消防抖函数
    this.#onResize.cancel();

    // 移除事件监听
    window.removeEventListener('resize', this.#onResize);
  }
}

if (!customElements.get('jxs-st-video-hero')) {
  customElements.define('jxs-st-video-hero', JxsStVideoHero);
}
